import React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { selectRoles } from "@/store/slices/roles/rolesSlice";
import CRMRoutes from "@/routes/crm-routes";
import { getVisibleChildRoutes } from "@/routes/nav-child-routes";
import useFeatures from "@/hooks/useFeatures";
import Logo from "@/components/logo";
import { ROUTE_ICONS } from "./constants";
import { FeatureKeys } from "@/services/dropdown-enums";
import SidebarIcon from "./SidebarIcon";
import SidebarMenu from "./SidebarMenu";
import { getNavLeafKey, sanitizePathForLeaf } from "@/routes/nav-route-persistence";

const SIDEBAR_WIDTH = 78;

const Sidebar = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const { roles } = useSelector(selectRoles);
  const { features, hasFeature } = useFeatures();
  const savedRoutes = useSelector((state) => state.routeHistory?.routes ?? {});

  const isTourActive = useSelector((state) => state.tour.startTour);
  const selectedTour = useSelector((state) => state.tour.selectedTour);
  const currentStepIndex = useSelector((state) => state.tour.currentStepIndex);

  const [anchorEl, setAnchorEl] = React.useState({});

  React.useEffect(() => {
    setAnchorEl({});
  }, [pathname]);

  // Returns true if this sidebar item is the current tour target
  // Handles both selector patterns: .tour-{className} (smarthub) and .{className} (appointment dashboard)
  const isItemBeingToured = (item) => {
    if (!isTourActive || !selectedTour?.length || !item.className) return false;
    const step = selectedTour[currentStepIndex];
    if (!step?.element || typeof step.element !== "string") return false;
    const el = step.element;
    return el.includes(`tour-${item.className}`) || el.includes(`.${item.className}`);
  };

  const handleMenuOpen = (event, name) => {
    event.preventDefault(); // prevent navigation if clicking parent
    setAnchorEl((prev) =>
      prev[name] ? {} : { [name]: event.currentTarget },
    );
  };

  const handleMenuClose = (e) => {
    if (e) e.stopPropagation();
    setAnchorEl({});
  };

  // Handles clicks on direct-link sidebar items (no dropdown).
  // Always restores the last-visited path for this leaf from Redux,
  // falling back to the canonical base path if nothing is saved yet.
  const handleDirectNavClick = (e, item, directPath) => {
    e.preventDefault();
    if (!directPath) return;
    const leafKey = getNavLeafKey(item);
    const destination = sanitizePathForLeaf(leafKey, savedRoutes[leafKey]) ?? directPath;
    if (pathname === destination) return;
    router.push(destination);
  };

  const isActive = (item) => {
    if (item.commonPath) return pathname?.startsWith(item.commonPath);
    if (item.path) return pathname === item.path || pathname?.startsWith(item.path + "/");
    return false;
  };

  const orderedRoutes = React.useMemo(() => {
    if (!hasFeature(FeatureKeys.CRM)) return CRMRoutes;
    const analyticsIndex = CRMRoutes.findIndex((r) => r.name === "analytics");
    if (analyticsIndex <= 0) return CRMRoutes;
    const analyticsRoute = CRMRoutes[analyticsIndex];
    return [
      analyticsRoute,
      ...CRMRoutes.slice(0, analyticsIndex),
      ...CRMRoutes.slice(analyticsIndex + 1),
    ];
  }, [features, hasFeature]);

  return (
    <Box
      component="nav"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width: SIDEBAR_WIDTH,
        // Using MUI CSS variables provided by custom themes
        backgroundColor: "var(--palette-header_bg, #24183d)", 
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        py: "12px",
        zIndex: 1100, // Above AppNavBar
      }}
    >
      {/* Top Section: Logo & Navigation items */}
      <Box>
        {/* LOGO AREA */}
        <Box sx={{ 
            height: "40px", // Align vertically with topbar
            display: "flex", 
            alignItems: "center",
            justifyContent: "center",
            mb: "12px", 
            ml: "40px"
        }}>
          {/* We pass a custom override to Logo to fix spacing/sizing within the Sidebar */}
          {/* We might use a small 'icon only' logo or custom scaling based on 86px width */}
          <Logo sx={{ marginRight: 0, marginTop: 0 }} />
        </Box>

        {/* NAVIGATION LIST */}
        <Box sx={{ 
            px: "15px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "24px",
            mt: "24px"
        }}>
            {orderedRoutes.map((item) => {
            // Check if the user has view permission for this parent module
            const roleKey = item.name;
            const hasViewPermission = roles && roles[roleKey] && roles[roleKey].view === true;
           
            // If they don't explicitly have view access, hide the section entirely
            if (!hasViewPermission) {
                 return null;
            }

            const visibleChildren = getVisibleChildRoutes(item, roles, features);
            if (item.childRoutes?.length > 0 && visibleChildren.length === 0) {
              return null;
            }
            const hasDropdown = visibleChildren.length > 1;
            const directPath =
              visibleChildren.length === 1
                ? visibleChildren[0].path
                : item.path;
            const isMenuOpen = Boolean(anchorEl[item.name]);

            // During the tour, auto-open submenu if this item is being toured
            const isToured = isItemBeingToured(item);
            const showSubmenu =
              hasDropdown && (isMenuOpen || (isToured && visibleChildren.length > 0));
            const isTourDriven = isToured && !anchorEl[item.name];
            const tourAnchorEl = isTourDriven
              ? (typeof document !== "undefined"
                  ? (document.querySelector(`.tour-${item.className} a`) ||
                     document.querySelector(`.${item.className} a`))
                  : null)
              : null;
            const effectiveAnchorEl = anchorEl[item.name] || tourAnchorEl;

            return (
                <Box key={item.name} className={`tour-${item?.className || ""} ${item?.className || ""}`} sx={{ position: "relative" }}>
                <Tooltip
                    title={item.title}
                    placement="right"
                    arrow
                    disableHoverListener={showSubmenu}
                >
                    <Link
                    href={hasDropdown ? "#" : (directPath || "#")}
                    onClick={(e) => {
                        if (hasDropdown) {
                          handleMenuOpen(e, item.name);
                        } else {
                          handleDirectNavClick(e, item, directPath);
                        }
                    }}
                    style={{ textDecoration: "none", display: "block" }}
                    >
                    <SidebarIcon
                        iconNode={ROUTE_ICONS[item.name] || ROUTE_ICONS.default}
                        isActive={isActive(item) || showSubmenu}
                    />
                    </Link>
                </Tooltip>
                
                {hasDropdown && (
                    <SidebarMenu
                    anchorEl={effectiveAnchorEl}
                    isMenuOpen={showSubmenu}
                    handleMenuClose={handleMenuClose}
                    menuOptions={visibleChildren}
                    pathname={pathname}
                    roles={roles}
                    parentName={item.name}
                    parentTitle={item.title}
                    isTourDriven={isTourDriven}
                    />
                )}
                </Box>
            );
            })}
        </Box>
      </Box>


    </Box>
  );
};

export default Sidebar;
